import { Link } from 'react-router';
import type { AuditRecord } from '../../data/audits';
import type { Store } from '../../data/types';
import { DistrictLink, StoreLink } from '../entityLinks';
import { formatDay, formatTime } from '../dates';
import { PersonName } from '../PersonName';
import { scorePct } from '../scoreText';
import { useOwnStoreOnly } from '../ownStore';

/**
 * One completed-audit card, shared by the Audits hub and the store/district
 * detail pages. Store and district names link to their dedicated pages;
 * View (a real link — opens in a new tab too) shows the read-only audit record. On a store's own page the store
 * (and its district) aren't repeated — the card leads with `title` (the
 * audit type); on a district page the district isn't repeated.
 */
export function CompletedAuditRow({
  audit,
  store,
  context,
  title,
  formName,
}: {
  audit: AuditRecord;
  store: Store;
  context?: 'store' | 'district' | undefined;
  /** The card's heading on a store page (e.g. the audit type's name). */
  title?: string;
  /** The form's name, shown first in the line under a store heading (UI review U32). */
  formName?: string | undefined;
}) {
  // A store team's own store (S8): the card leads with the audit, as on the store's own page.
  const ownStore = useOwnStoreOnly(store);
  if (ownStore && context === undefined) {
    context = 'store';
    title ??= formName;
  }
  const showDistrict = context === undefined && store.district.trim() !== '';
  return (
    <div className="audit-row">
      <div>
        <strong>{context === 'store' ? title || 'Audit' : <StoreLink store={store} />}</strong>
        <div className="muted">
          {context !== 'store' && formName && (
            <>
              {formName}
              {' · '}
            </>
          )}
          {showDistrict && (
            <>
              <span className="nowrap">
                <DistrictLink district={store.district} />
              </span>
              {' · '}
            </>
          )}
          {/* Date, time, name and score never split mid-part (UI review U34 / V57). */}
          {/* Each part keeps its leading "·" so a wrapped line never starts with one (UI-9 review). */}
          {/* Two unbreakable pieces, so the line can break between date and time beside "View" on a phone (UI-10 review). */}
          <span className="nowrap">submitted {formatDay(audit.submittedAt)},</span>{' '}
          <span className="nowrap">{formatTime(audit.submittedAt)}</span>{' '}
          <span className="nowrap">
            · by <PersonName upn={audit.auditorUpn} />
          </span>
          {/* Phones (UI review 3, S17): "no score" adds a line and says little — a score always shows. */}
          {audit.score === null ? (
            <>
              {' '}
              <span className="not-phone nowrap">· no score</span>
            </>
          ) : (
            <>
              {' '}
              <span className="nowrap">· score {scorePct(audit.score)}</span>
            </>
          )}
          {audit.criticalFailed && (
            <>
              {' '}
              <span className="error nowrap">· critical failure</span>
            </>
          )}
        </div>
      </div>
      <Link className="btn" to={`/audits/records/${audit.id}`}>
        View
      </Link>
    </div>
  );
}
